import type React from 'react';
import { createContext, useContext, useRef, useCallback, useMemo, useEffect } from 'react';
import { ThreadPositionManager } from '../helpers/ThreadPositionManager';

interface ThreadPositionContextValue {
  getPositionManager: () => ThreadPositionManager;
  clearPositions: () => void;
}

const ThreadPositionContext = createContext<ThreadPositionContextValue | null>(null);

interface ThreadPositionProviderProps {
  children: React.ReactNode;
}

export const ThreadPositionProvider: React.FC<ThreadPositionProviderProps> = ({ children }) => {
  const positionManagerRef = useRef<ThreadPositionManager | null>(null);

  const getPositionManager = useCallback((): ThreadPositionManager => {
    if (!positionManagerRef.current) {
      positionManagerRef.current = new ThreadPositionManager();
    }
    return positionManagerRef.current;
  }, [positionManagerRef]);

  const clearPositions = useCallback(() => {
    if (positionManagerRef.current) {
      positionManagerRef.current.clear();
    }
  }, [positionManagerRef]);

  const value: ThreadPositionContextValue = useMemo(
    () => ({
      getPositionManager,
      clearPositions,
    }),
    [getPositionManager, clearPositions]
  );

  useEffect(() => {
    return () => {
      clearPositions();
    };
  }, []);

  return <ThreadPositionContext.Provider value={value}>{children}</ThreadPositionContext.Provider>;
};

export const useThreadPosition = (): ThreadPositionContextValue => {
  const context = useContext(ThreadPositionContext);
  if (!context) {
    throw new Error('useThreadPosition must be used within a ThreadPositionProvider');
  }
  return context;
};
